import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { calendarService, IGetCalendarFilterOptionsResponse } from '../../../entities/calendar'
import { useGlobalMessageActions } from '../../../entities/globalMessage'
import { useMyActions } from '../../../entities/my'
import {
    getTrialCalendarFilter,
    getTrialOptionItems,
    getTrialSelectedIdItems,
    ITrialCreationDraft,
    TTutorGender,
} from '../../../entities/trialLesson'
import { AuthError } from '../../../shared/lib/error/AuthError'
import { MyError } from '../../../shared/lib/error/MyError'
import { IItem } from '../../../shared/model/types'
import { DropDownListSelected } from '../../../shared/ui/dropDownSelected'
import classes from './trialCreationSetup.module.scss'

interface IProps {
    value: ITrialCreationDraft;
    onChange: (value: ITrialCreationDraft) => void;
    disabled?: boolean;
}

const GENDER_ITEMS: Array<IItem<TTutorGender>> = [
    {id: 'TUTOR_GENDER_ANY', name: 'Любой'},
    {id: 'TUTOR_GENDER_MALE', name: 'Мужской'},
    {id: 'TUTOR_GENDER_FEMALE', name: 'Женский'},
]

const DURATION_ITEMS: Array<IItem<number>> = [
    {id: 1, name: '30 минут'},
    {id: 2, name: '60 минут'},
    {id: 3, name: '90 минут'},
    {id: 4, name: '120 минут'},
]

export const TrialCreationSetup: FC<IProps> = ({value, onChange, disabled = false}) => {
    const [tgAccounts, setTgAccounts] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const [tags, setTags] = useState<IGetCalendarFilterOptionsResponse['tags']>([])
    const [loadingField, setLoadingField] = useState<'tg' | 'tag' | null>('tg')
    const tgRequestRef = useRef<AbortController | null>(null)
    const tagRequestRef = useRef<AbortController | null>(null)
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const handleError = (error: unknown) => {
        if(error instanceof DOMException && error.name === 'AbortError') return
        if(error instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: error.message, type: 'error'})
            return
        }
        setGlobalMessage({
            message: error instanceof MyError ? error.message : 'Ошибка при загрузке параметров пробного',
            type: 'error',
        })
    }

    useEffect(() => {
        const controller = new AbortController()
        tgRequestRef.current = controller
        setLoadingField('tg')

        calendarService.getCalendarFilterOptions(
            {selected: getTrialCalendarFilter('', '')},
            controller.signal,
        ).then(response => {
            if(!controller.signal.aborted) setTgAccounts(response.tgAccounts || [])
        }).catch(handleError).finally(() => {
            if(!controller.signal.aborted) setLoadingField(null)
        })

        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if(!value.tgAccountId){
            setTags([])
            return
        }

        tagRequestRef.current?.abort()
        const controller = new AbortController()
        tagRequestRef.current = controller
        setLoadingField('tag')

        calendarService.getCalendarFilterOptions(
            {selected: getTrialCalendarFilter(value.tgAccountId, '')},
            controller.signal,
        ).then(response => {
            if(!controller.signal.aborted) setTags(response.tags || [])
        }).catch(handleError).finally(() => {
            if(!controller.signal.aborted) setLoadingField(null)
        })

        return () => controller.abort()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.tgAccountId])

    const tgItems = useMemo(
        () => getTrialOptionItems(tgAccounts, item => item.title || `@${item.username}`),
        [tgAccounts],
    )
    const tagItems = useMemo(
        () => getTrialOptionItems(tags, item => item.name),
        [tags],
    )

    return (
        <section className={`${classes.grid} ${disabled ? classes.disabled : ''}`}>
            <label className={classes.field}>
                <span>Рабочий Telegram</span>
                <DropDownListSelected
                    isLoading={loadingField === 'tg'}
                    items={tgItems}
                    selectedIdItems={getTrialSelectedIdItems(value.tgAccountId)}
                    onSelected={item => selected => {
                        if(disabled) return
                        onChange({
                            ...value,
                            tgAccountId: selected ? item.id : '',
                            tagId: '',
                        })
                    }}
                    placeholder="Выберите Telegram"
                    search
                    emptyText="Нет доступных Telegram"
                />
            </label>

            <label className={classes.field}>
                <span>Тег</span>
                <DropDownListSelected
                    isLoading={loadingField === 'tag'}
                    items={tagItems}
                    selectedIdItems={getTrialSelectedIdItems(value.tagId)}
                    onSelected={item => selected => {
                        if(disabled) return
                        onChange({...value, tagId: selected ? item.id : ''})
                    }}
                    placeholder={value.tgAccountId ? 'Выберите тег' : 'Сначала выберите Telegram'}
                    search
                    emptyText="Нет студентов или исполнителей с тегами на тг"
                />
            </label>

            <label className={classes.field}>
                <span>Пол исполнителя</span>
                <DropDownListSelected
                    isLoading={false}
                    items={GENDER_ITEMS}
                    selectedIdItems={value.tutorGender === 'TUTOR_GENDER_UNSPECIFIED'
                        ? []
                        : [value.tutorGender]
                    }
                    onSelected={item => selected => {
                        if(disabled) return
                        onChange({
                            ...value,
                            tutorGender: selected ? item.id : 'TUTOR_GENDER_UNSPECIFIED',
                        })
                    }}
                    placeholder="Выберите пол"
                />
            </label>

            <label className={classes.field}>
                <span>Продолжительность</span>
                <DropDownListSelected
                    isLoading={false}
                    items={DURATION_ITEMS}
                    selectedIdItems={[value.durationSlots]}
                    onSelected={item => selected => {
                        if(disabled || !selected) return
                        onChange({...value, durationSlots: item.id})
                    }}
                />
            </label>
        </section>
    )
}
